import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings,
  MonitorHeart,
  People,
  History,
  Tune,
  Search,
  Add,
  Refresh,
  Download,
  CheckCircle,
  Cancel,
  HelpOutline,
  Edit,
  Storage,
  Cloud,
  Speed,
  WifiTethering,
} from '@mui/icons-material';

// ─── TypeScript Interfaces ────────────────────────────────────────────────────

interface ServiceStatus {
  name: string;
  displayName: string;
  port: number;
  status: 'healthy' | 'unhealthy' | 'unknown' | 'checking';
  lastChecked: string | null;
  responseTime?: number;
}

interface MesUser {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'QUALITY' | 'PLANNER';
  status: 'active' | 'inactive';
  lastLogin: string | null;
  createdAt: string;
}

interface AuditLogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT';
  resource: string;
  ipAddress: string;
  result: 'success' | 'failure';
}

interface InfraStatus {
  name: string;
  type: 'database' | 'kafka' | 'redis';
  status: 'operational' | 'down' | 'unknown' | 'checking';
  latency?: number;
}

interface NewUserForm {
  name: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'QUALITY' | 'PLANNER';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const API_GATEWAY = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

const SERVICES: Omit<ServiceStatus, 'status' | 'lastChecked'>[] = [
  { name: 'auth', displayName: 'Auth Service', port: 3001 },
  { name: 'master-data', displayName: 'Master Data', port: 3002 },
  { name: 'work-orders', displayName: 'Work Orders', port: 3003 },
  { name: 'shop-floor', displayName: 'Shop Floor', port: 3004 },
  { name: 'analytics', displayName: 'Analytics', port: 3008 },
  { name: 'analytics-ai', displayName: 'Analytics AI', port: 3007 },
  { name: 'manufacturing-planning', displayName: 'Manufacturing Planning', port: 3006 },
  { name: 'quality', displayName: 'Quality', port: 3407 },
  { name: 'predictive-maintenance', displayName: 'Predictive Maintenance', port: 3408 },
  { name: 'netsuite-integration', displayName: 'NetSuite Integration', port: 3411 },
  { name: 'kitchen-ops', displayName: 'Kitchen Ops', port: 3400 },
  { name: 'milling', displayName: 'Milling', port: 3401 },
  { name: 'scheduling', displayName: 'Scheduling', port: 3405 },
];


const SERVICE_PORT_MAP: { service: string; port: number; description: string }[] = [
  { service: 'API Gateway', port: 3100, description: 'Main entry point for all API requests' },
  { service: 'Auth Service', port: 3001, description: 'Authentication and authorisation' },
  { service: 'Master Data', port: 3000, description: 'Items, BOMs, routings, locations' },
  { service: 'Work Orders', port: 3002, description: 'Production work order management' },
  { service: 'Shop Floor', port: 3005, description: 'Operator workstation and floor data' },
  { service: 'Analytics', port: 3009, description: 'OEE, KPIs and reporting' },
  { service: 'Analytics AI', port: 3007, description: 'AI-driven insights and predictions' },
  { service: 'Manufacturing Planning', port: 3006, description: 'MPS, MRP and capacity planning' },
  { service: 'Quality', port: 3407, description: 'Inspections, defects, SPC' },
  { service: 'Predictive Maintenance', port: 3408, description: 'Equipment health monitoring' },
  { service: 'NetSuite Integration', port: 3411, description: 'NetSuite ERP integration' },
  { service: 'Kitchen Ops', port: 3400, description: 'School kitchen network management' },
  { service: 'Milling', port: 3010, description: 'Flour milling recipes and orders' },
  { service: 'Scheduling', port: 3405, description: 'Production scheduling and dispatch' },
];

// ─── Helper utilities ─────────────────────────────────────────────────────────

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'medium' });
}

function csvEscape(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

function downloadCSV(rows: string[][], filename: string): void {
  const content = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Status chip helper ───────────────────────────────────────────────────────

function ServiceStatusChip({ status }: { status: ServiceStatus['status'] }) {
  const map: Record<
    ServiceStatus['status'],
    { label: string; color: 'success' | 'error' | 'default' | 'warning'; icon: React.ReactElement }
  > = {
    healthy: { label: 'Online', color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    unhealthy: { label: 'Offline', color: 'error', icon: <Cancel sx={{ fontSize: 14 }} /> },
    unknown: { label: 'Unknown', color: 'default', icon: <HelpOutline sx={{ fontSize: 14 }} /> },
    checking: { label: 'Checking…', color: 'warning', icon: <CircularProgress size={10} /> },
  };
  const { label, color, icon } = map[status];
  return <Chip label={label} color={color} size="small" icon={icon} />;
}

function ActionChip({ action }: { action: AuditLogEntry['action'] }) {
  const colorMap: Record<AuditLogEntry['action'], 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    CREATE: 'success',
    UPDATE: 'warning',
    DELETE: 'error',
    LOGIN: 'info',
    LOGOUT: 'default',
  };
  return <Chip label={action} color={colorMap[action]} size="small" />;
}

// ─── Tab 1: System Health ─────────────────────────────────────────────────────

function SystemHealthTab() {
  const [services, setServices] = useState<ServiceStatus[]>(
    SERVICES.map((s) => ({ ...s, status: 'checking', lastChecked: null }))
  );
  const [refreshing, setRefreshing] = useState(false);

  const checkServices = useCallback(async () => {
    setRefreshing(true);
    const start = Date.now();

    // Attempt a gateway-level health summary first, fall back to individual checks
    const results = await Promise.allSettled(
      SERVICES.map(async (svc) => {
        const t0 = Date.now();
        try {
          const res = await fetch(`${API_GATEWAY}/health/${svc.name}`, { signal: AbortSignal.timeout(4000) });
          return { name: svc.name, status: res.ok ? 'healthy' : 'unhealthy', responseTime: Date.now() - t0 };
        } catch {
          return { name: svc.name, status: 'unhealthy' as const, responseTime: Date.now() - t0 };
        }
      })
    );

    const now = new Date().toISOString();
    setServices((prev) =>
      prev.map((svc, idx) => {
        const r = results[idx];
        if (r.status === 'fulfilled') {
          return { ...svc, status: r.value.status as ServiceStatus['status'], lastChecked: now, responseTime: r.value.responseTime };
        }
        return { ...svc, status: 'unknown', lastChecked: now };
      })
    );
    setRefreshing(false);
  }, []);

  useEffect(() => {
    checkServices();
    const interval = setInterval(checkServices, 30_000);
    return () => clearInterval(interval);
  }, [checkServices]);

  const healthyCount = services.filter((s) => s.status === 'healthy').length;
  const unhealthyCount = services.filter((s) => s.status === 'unhealthy').length;

  return (
    <Box>
      {/* Summary row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="textSecondary">Total Services</Typography>
              <Typography variant="h5" color="primary.main">{SERVICES.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="textSecondary">Online</Typography>
              <Typography variant="h5" color="success.main">{healthyCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="textSecondary">Offline / Degraded</Typography>
              <Typography variant="h5" color="error.main">{unhealthyCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="textSecondary">Last Refresh</Typography>
              <Typography variant="body2">{services[0]?.lastChecked ? formatDateTime(services[0].lastChecked) : '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Refresh button */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={checkServices}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh Now'}
        </Button>
      </Box>

      {/* Service cards */}
      <Grid container spacing={2}>
        {services.map((svc) => (
          <Grid item xs={12} sm={6} md={4} key={svc.name}>
            <Card
              sx={{
                borderLeft: 4,
                borderColor:
                  svc.status === 'healthy'
                    ? 'success.main'
                    : svc.status === 'unhealthy'
                    ? 'error.main'
                    : 'grey.400',
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {svc.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Port {svc.port}
                    </Typography>
                  </Box>
                  <ServiceStatusChip status={svc.status} />
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="textSecondary" display="block">
                  Last checked: {svc.lastChecked ? formatDateTime(svc.lastChecked) : 'Pending…'}
                </Typography>
                {svc.responseTime !== undefined && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Response time: {svc.responseTime} ms
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" color="textSecondary" display="block" mt={2}>
        Auto-refreshes every 30 seconds. Requests are sent through the API Gateway at {API_GATEWAY}.
      </Typography>
    </Box>
  );
}

// ─── Tab 2: User Management ───────────────────────────────────────────────────

function UserManagementTab() {
  const [users, setUsers] = useState<MesUser[]>([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MesUser | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<NewUserForm>>({});
  const [newUser, setNewUser] = useState<NewUserForm>({
    name: '',
    email: '',
    password: '',
    role: 'OPERATOR',
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_GATEWAY}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const data: MesUser[] = json.data ?? json;
      setUsers(Array.isArray(data) ? data : []);
      setUsingFallback(false);
    } catch {
      setUsingFallback(true);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  const openAddDialog = () => {
    setEditTarget(null);
    setNewUser({ name: '', email: '', password: '', role: 'OPERATOR' });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (user: MesUser) => {
    setEditTarget(user);
    setNewUser({ name: user.name, email: user.email, password: '', role: user.role });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validate = (): boolean => {
    const errors: Partial<NewUserForm> = {};
    if (!newUser.name.trim()) errors.name = 'Name is required';
    if (!newUser.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email))
      errors.email = 'Valid email required';
    if (!editTarget && !newUser.password.trim()) errors.password = 'Password is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    try {
      if (editTarget) {
        await fetch(`${API_GATEWAY}/auth/users/${editTarget.id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ name: newUser.name, email: newUser.email, role: newUser.role }),
        });
      } else {
        await fetch(`${API_GATEWAY}/auth/users`, {
          method: 'POST',
          headers,
          body: JSON.stringify(newUser),
        });
      }
      setDialogOpen(false);
      loadUsers();
    } catch {
      // keep dialog open on error
    }
  };

  const toggleStatus = async (userId: string, currentStatus: string) => {
    const token = localStorage.getItem('token');
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    await fetch(`${API_GATEWAY}/auth/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: newStatus }),
    }).catch(() => {});
    loadUsers();
  };

  const roleColors: Record<MesUser['role'], 'primary' | 'error' | 'success' | 'warning' | 'info'> = {
    ADMIN: 'error',
    MANAGER: 'warning',
    OPERATOR: 'primary',
    QUALITY: 'info',
    PLANNER: 'success',
  };

  return (
    <Box>
      {usingFallback && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load users from /auth/users — check that the auth service is online.
        </Alert>
      )}
      {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
          Add User
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Last Login</strong></TableCell>
              <TableCell><strong>Created At</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary" py={2}>No users match your search.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role} color={roleColors[user.role]} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status === 'active' ? 'Active' : 'Inactive'}
                      color={user.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(user.lastLogin)}</TableCell>
                  <TableCell>{formatDateTime(user.createdAt)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} alignItems="center">
                      <Tooltip title="Edit role">
                        <IconButton size="small" onClick={() => openEditDialog(user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.status === 'active' ? 'Deactivate' : 'Activate'}>
                        <Switch
                          size="small"
                          checked={user.status === 'active'}
                          onChange={() => toggleStatus(user.id, user.status)}
                          color="success"
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTarget ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Full Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
            />
            {!editTarget && (
              <TextField
                label="Password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                error={!!formErrors.password}
                helperText={formErrors.password}
                fullWidth
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={newUser.role}
                onChange={(e) =>
                  setNewUser({ ...newUser, role: e.target.value as NewUserForm['role'] })
                }
              >
                {(['ADMIN', 'MANAGER', 'OPERATOR', 'QUALITY', 'PLANNER'] as const).map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editTarget ? 'Save Changes' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Tab 3: Audit Log ─────────────────────────────────────────────────────────

function AuditLogTab() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('ALL');

  useEffect(() => {
    const loadLogs = async () => {
      setLogsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_GATEWAY}/auth/audit-logs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        const data: AuditLogEntry[] = json.data ?? json;
        setLogs(Array.isArray(data) ? data : []);
      } catch {
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    };
    loadLogs();
  }, []);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const filtered = logs.filter((log) => {
    if (actionFilter !== 'ALL' && log.action !== actionFilter) return false;
    if (startDate && new Date(log.timestamp) < new Date(startDate)) return false;
    if (endDate && new Date(log.timestamp) > new Date(endDate + 'T23:59:59Z')) return false;
    return true;
  });

  const handleExportCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Resource', 'IP Address', 'Result'];
    const rows = filtered.map((log) => [
      log.timestamp,
      log.user,
      log.action,
      log.resource,
      log.ipAddress,
      log.result,
    ]);
    downloadCSV([headers, ...rows], `audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <Box>
      {/* Filters */}
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Action Type</InputLabel>
          <Select
            label="Action Type"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <MenuItem value="ALL">All Actions</MenuItem>
            {(['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT'] as const).map((a) => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Box flexGrow={1} />
        <Button variant="outlined" startIcon={<Download />} onClick={handleExportCSV}>
          Export CSV
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell><strong>Timestamp</strong></TableCell>
              <TableCell><strong>User</strong></TableCell>
              <TableCell><strong>Action</strong></TableCell>
              <TableCell><strong>Resource</strong></TableCell>
              <TableCell><strong>IP Address</strong></TableCell>
              <TableCell><strong>Result</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" py={2}>No log entries match the current filters.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.timestamp)}</TableCell>
                  <TableCell>{log.user}</TableCell>
                  <TableCell><ActionChip action={log.action} /></TableCell>
                  <TableCell>{log.resource}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{log.ipAddress}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.result}
                      color={log.result === 'success' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {logsLoading
        ? <CircularProgress size={24} sx={{ mt: 2 }} />
        : <Typography variant="caption" color="textSecondary" mt={1} display="block">
            {logs.length === 0 ? 'No audit log entries found.' : `Showing ${filtered.length} of ${logs.length} entries.`}
          </Typography>
      }
    </Box>
  );
}

// ─── Tab 4: System Configuration ─────────────────────────────────────────────

function SystemConfigTab() {
  const [infraStatus, setInfraStatus] = useState<InfraStatus[]>([
    { name: 'PostgreSQL', type: 'database', status: 'checking' },
    { name: 'Apache Kafka', type: 'kafka', status: 'checking' },
    { name: 'Redis Cache', type: 'redis', status: 'checking' },
  ]);
  const [checking, setChecking] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const checkConnectivity = async () => {
    setChecking(true);
    setInfraStatus((prev) => prev.map((i) => ({ ...i, status: 'checking' })));
    try {
      const resp = await fetch(`${API_GATEWAY}/health/infrastructure`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setInfraStatus([
          { name: 'PostgreSQL', type: 'database', status: data.postgresql === 'operational' ? 'operational' : data.postgresql === 'down' ? 'down' : 'unknown' },
          { name: 'Apache Kafka', type: 'kafka', status: data.kafka === 'operational' ? 'operational' : data.kafka === 'down' ? 'down' : 'unknown' },
          { name: 'Redis Cache', type: 'redis', status: data.redis === 'operational' ? 'operational' : data.redis === 'down' ? 'down' : 'unknown' },
        ]);
      } else {
        setInfraStatus((prev) => prev.map((i) => ({ ...i, status: 'unknown' })));
      }
    } catch {
      setInfraStatus((prev) => prev.map((i) => ({ ...i, status: 'unknown' })));
    }
    setChecking(false);
  };

  useEffect(() => {
    checkConnectivity();
    const interval = setInterval(checkConnectivity, 30_000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const infraIcon: Record<InfraStatus['type'], React.ReactNode> = {
    database: <Storage color="action" />,
    kafka: <Cloud color="action" />,
    redis: <Speed color="action" />,
  };

  return (
    <Box>
      {/* Summary info */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                System Date &amp; Time
              </Typography>
              <Typography variant="h6" fontFamily="monospace">
                {currentTime.toLocaleString('en-KE', { timeZone: 'Africa/Nairobi' })}
              </Typography>
              <Typography variant="caption" color="textSecondary">East Africa Time (EAT)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                System Version
              </Typography>
              <Typography variant="h6">MES v1.0.0</Typography>
              <Typography variant="caption" color="textSecondary">
                API Gateway: {API_GATEWAY}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Infrastructure connectivity */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Infrastructure Connectivity</Typography>
          <Button
            variant="outlined"
            startIcon={checking ? <CircularProgress size={16} /> : <WifiTethering />}
            onClick={checkConnectivity}
            disabled={checking}
          >
            {checking ? 'Checking…' : 'Check Connectivity'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          {infraStatus.map((infra) => (
            <Grid item xs={12} sm={4} key={infra.name}>
              <Card
                sx={{
                  borderLeft: 4,
                  borderColor:
                    infra.status === 'operational'
                      ? 'success.main'
                      : infra.status === 'down'
                      ? 'error.main'
                      : 'grey.400',
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {infraIcon[infra.type]}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {infra.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      infra.status === 'checking'
                        ? 'Checking…'
                        : infra.status === 'operational'
                        ? 'Operational'
                        : infra.status === 'down'
                        ? 'Down'
                        : 'Unknown'
                    }
                    color={
                      infra.status === 'operational'
                        ? 'success'
                        : infra.status === 'down'
                        ? 'error'
                        : 'warning'
                    }
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Service ports table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" mb={2}>Service Port Configuration</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell><strong>Service</strong></TableCell>
                <TableCell><strong>Port</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SERVICE_PORT_MAP.map((row) => (
                <TableRow key={row.service} hover>
                  <TableCell>{row.service}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.port}</TableCell>
                  <TableCell>{row.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────

const Admin: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  // RBAC guard — only ADMIN and MANAGER roles may access this page
  const token = localStorage.getItem('token');
  let userRole = '';
  try {
    const p = JSON.parse(atob((token || '').split('.')[1] || '{}'));
    userRole = (p.role || p.roles?.[0] || '').toUpperCase();
  } catch { /* invalid token — deny */ }

  if (!token || !['ADMIN', 'MANAGER'].includes(userRole)) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 480, mx: 'auto' }}>
          <strong>Access Denied</strong> — Administrator privileges required to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <AdminPanelSettings color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" component="h1">
              Admin Dashboard
            </Typography>
            <Typography variant="body2" color="textSecondary">
              System administration, user management and monitoring
            </Typography>
          </Box>
        </Box>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        This area is restricted to administrators. All actions are logged.
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<MonitorHeart />} iconPosition="start" label="System Health" />
          <Tab icon={<People />} iconPosition="start" label="User Management" />
          <Tab icon={<History />} iconPosition="start" label="Audit Log" />
          <Tab icon={<Tune />} iconPosition="start" label="System Configuration" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && <SystemHealthTab />}
      {selectedTab === 1 && <UserManagementTab />}
      {selectedTab === 2 && <AuditLogTab />}
      {selectedTab === 3 && <SystemConfigTab />}
    </Box>
  );
};

export default Admin;
